import React, {useCallback, useRef, useState} from 'react';
import {Platform, View} from 'react-native';
import Animated from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';
import {Image, ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import DepartmentCarousel from '../../components/DepartmentCarousel';
import HomepageCarousel from '../../components/HomepageCarousel';
import {
  BANNER_ASPECT_RATIO_MOBILE,
  BANNER_ASPECT_RATIO_WEB,
  BANNER_DESKTOP_MAX_HEIGHT,
  BANNER_DESKTOP_MAX_WIDTH,
} from '../../components/HomepageCarousel/constants';
import {MemberPriceCarousel} from '../../components/ProductCarousel';
import PromotionsCarousel from '../../components/PromotionsCarousel';
import ShopLoyaltyEnabled from '../../components/rewards/ShopLoyaltyEnabled';
import TitleLine from '../../components/rewards/TitleLine';
import ShopByBrand from '../../components/ShopByBrand';
import {BannerSkeleton} from '../../components/skeleton';
import ContentView from '../../components/ui-controls/layout/ContentView';
import WebFooter from '../../components/WebFooter';
import config from '../../config';
import {useScrollCollapseHandler} from '../../hooks/useScrollCollapse';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtableBrand, AirtablePromotionItem} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {GeocodedSuburb} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useHomepage} from '../../queries/homepage/useHomepage';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName} from '../../utils/analytics/events';
import {openPageLink} from '../../utils/navigation';
import {brandRewardsCopy} from '../../utils/rewards';
import SuburbSelectionModal from '../storeSelection/SuburbSelectionModal';

import Banner from './Banner';
import {formatHeroImage} from './shared';

const modalBodyText = `All of our stores are independently owned and operated. \
To view accurate pricing, promotions and product availability, \
find your local ${config.APP_NAME} store by entering your suburb or postcode below.`;

const HomepageScreen = observer(({navigation, route}: RouteNavigatorScreenProps<AppRoute.HOME>) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const tabBarPadding = useTabBarBottomPadding({skipFocusReset: true});
  const {shopSelectionStore, userStore, rewardsStore} = useStores();
  const listRef = useRef(null);
  const {scrollHandler} = useScrollCollapseHandler(listRef);
  const headerHeight = useHeaderHeight();
  const [isSuburbModalVisible, setIsSuburbModalVisible] = useState(false);

  const selectedStore = shopSelectionStore?.selectedStore;
  const isFocused = useIsFocused();
  // Force the blocking modal open only while this screen is the focused onboarding
  // surface. SuburbSelectionModal portals to root, so without the focus gate it
  // floats above StoreSelectionScreen (pushed when a suburb is picked) and traps
  // the user — non-dismissable, store list unreachable (DIS-1344 web regression).
  const showBlockingModal = !selectedStore && isFocused;

  // Open modal when navigated with openSuburbModal param
  useFocusEffect(
    useCallback(() => {
      if (route.params?.openSuburbModal) {
        setIsSuburbModalVisible(true);
        navigation.setParams({openSuburbModal: undefined});
      }
    }, [route.params?.openSuburbModal, navigation]),
  );

  const handleSelectSuburb = (suburb: GeocodedSuburb) => {
    shopSelectionStore.resetLoading();
    shopSelectionStore.selectSuburb(suburb);

    Analytics.logEvent({
      eventName: EventName.LocationSelected,
      parameters: {
        screen_name: EventScreenName.SuburbSelection,
        location_id: `${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`,
        location_value: `${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`,
      },
    });

    navigation.navigate(AppRoute.MY_STORE_STORE_SELECTION);
    setIsSuburbModalVisible(false);
  };

  const handleCancelSuburbModal = () => {
    setIsSuburbModalVisible(false);
  };

  const {data: homepage, isLoading} = useHomepage({
    storeNumber: selectedStore?.store_number,
    storeState: selectedStore?.address?.administrative_area?.toUpperCase(),
  });

  const alertBannerItem = homepage?.data?.AlertBanner;
  // The AlertBanner image renders inline on the homepage whenever the record
  // carries an image, independent of any message (restores the pre-DIS-1457
  // behaviour). A text alert additionally shows in the global strip
  // (GlobalAlertBanner) — the two are not mutually exclusive.
  const alertHasImage = Boolean(
    alertBannerItem?.heroImage?.url || alertBannerItem?.heroImageWeb?.url,
  );

  const navigateToRewards = () => navigation.navigate(AppRoute.REWARDS);
  const navigateToDiscover = () => navigation.navigate(AppRoute.DISCOVER);

  const navigateFromCarousel = (pageLink: string) => {
    openPageLink(pageLink);
  };

  return (
    <ThemeProvider theme={rewardsTheme}>
      {/* Drop the shared ContentView top pad on the homepage only: the hero
          banner sits flush under the header (which the ScrollView already
          reserves via paddingTop). Bottom pad is kept for footer spacing. */}
      <ContentView testID="home-screen" parentStyle={{paddingTop: 0}}>
        <Animated.ScrollView
          ref={listRef}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          contentContainerStyle={{
            gap: isWebDesktopScreen ? 40 : 24,
            paddingTop: headerHeight,
            paddingBottom: tabBarPadding,
          }}
          showsVerticalScrollIndicator={isWebDesktopScreen}
          {...(Platform.OS === 'android' && {overScrollMode: 'never'})}>
          {alertHasImage ? <Banner bannerItem={alertBannerItem} /> : null}
          <Banner bannerItem={homepage?.data?.TopBanner} />

          {isLoading ? (
            <View>
              <BannerSkeleton
                showDots
                aspectRatio={
                  isWebDesktopScreen ? BANNER_ASPECT_RATIO_WEB : BANNER_ASPECT_RATIO_MOBILE
                }
                maxHeight={isWebDesktopScreen ? BANNER_DESKTOP_MAX_HEIGHT : undefined}
              />
            </View>
          ) : homepage?.carousel?.slides?.length ? (
            <View>
              <HomepageCarousel
                slides={homepage?.carousel?.slides ?? []}
                onSlidePress={navigateFromCarousel}
                testID="featured-carousel"
              />
            </View>
          ) : null}

          <ShopLoyaltyEnabled>
            {rewardsStore.isHydrated && !(userStore.isLoggedIn && rewardsStore.hasRewards) ? (
              <View>
                <TitleLine
                  title={brandRewardsCopy.loyaltyProgramName.toUpperCase()}
                  onSeeAllPress={navigateToRewards}
                />
                <Image
                  onPress={navigateToRewards}
                  source={formatHeroImage(homepage?.data?.LoyaltyAd, isWebDesktopScreen)}
                  containerStyle={{
                    width: '100%',
                    aspectRatio: isWebDesktopScreen
                      ? BANNER_ASPECT_RATIO_WEB
                      : BANNER_ASPECT_RATIO_MOBILE,
                    flex: 1,
                    flexGrow: 1,
                    borderRadius: 10,
                    ...(isWebDesktopScreen
                      ? {
                          maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
                          maxWidth: BANNER_DESKTOP_MAX_WIDTH,
                          alignSelf: 'center',
                        }
                      : {}),
                  }}
                />
              </View>
            ) : null}
            {!shopSelectionStore.ecommerceDisabled && (
              <MemberPriceCarousel
                title={brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}
                member_price="public"
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                    external_sell_type: 'members-price',
                  });
                }}
              />
            )}
          </ShopLoyaltyEnabled>

          <PromotionsCarousel
            onPromotionPress={(promotion: AirtablePromotionItem) => {
              navigation.navigate(AppRoute.PROMOTION, {
                promotion,
              });
            }}
            onSeeAllPress={() => {
              navigation.navigate(AppRoute.PROMOTIONS_CURRENT);
            }}
          />

          {!shopSelectionStore.ecommerceDisabled && (
            <DepartmentCarousel
              departmentName="whats-new"
              onSeeAllPress={department => {
                navigation.navigate(AppRoute.DEPARTMENT, {
                  department,
                });
              }}
            />
          )}

          <View>
            <TitleLine title="DISCOVER" onSeeAllPress={navigateToDiscover} />
            <Image
              onPress={navigateToDiscover}
              source={formatHeroImage(homepage?.data?.DiscoverFeature, isWebDesktopScreen)}
              containerStyle={{
                width: '100%',
                aspectRatio: isWebDesktopScreen
                  ? BANNER_ASPECT_RATIO_WEB
                  : BANNER_ASPECT_RATIO_MOBILE,
                flex: 1,
                flexGrow: 1,
                borderRadius: 10,
                ...(isWebDesktopScreen
                  ? {
                      maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
                      maxWidth: BANNER_DESKTOP_MAX_WIDTH,
                      alignSelf: 'center',
                    }
                  : {}),
              }}
            />
          </View>

          <ShopByBrand
            isLoading={isLoading}
            brands={homepage?.data?.HeroBrands?.brands || []}
            onSeeAllPress={() => {
              navigation.navigate(AppRoute.FAVOURITE_BRANDS);
            }}
            onBrandPress={(brand: AirtableBrand) => {
              navigation.navigate(AppRoute.BRAND, {
                brand,
              });
            }}
          />

          {!!isWebDesktopScreen && <WebFooter />}
        </Animated.ScrollView>

        <SuburbSelectionModal
          isVisible={showBlockingModal || isSuburbModalVisible}
          onSelectSuburb={handleSelectSuburb}
          onCancel={selectedStore ? handleCancelSuburbModal : undefined}
          title="FIND YOUR STORE"
          bodyText={modalBodyText}
        />
      </ContentView>
    </ThemeProvider>
  );
});

export default HomepageScreen;
